import { Link } from "react-router-dom";
import { IMAGES, WHATSAPP_URL } from "@/lib/constants";
import { Mail, Phone, MapPin } from "lucide-react";

const quickLinks = [
  { name: "Home", path: "/" },
  { name: "About Us", path: "/about" },
  { name: "Programmes", path: "/programmes" },
  { name: "Impact", path: "/impact" },
  { name: "Get Involved", path: "/get-involved" },
  { name: "Stories", path: "/stories" },
  { name: "Contact", path: "/contact" },
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#112A20] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img src={IMAGES.logo} alt="MBT Trust" className="h-12 w-auto brightness-0 invert" />
            </Link>
            <p className="text-white/70 text-sm font-outfit leading-relaxed mb-4">
              MBT Welfare & Development Trust is a grassroots social impact
              organisation rooted in the soil of Jharkhand, dedicated to
              transforming lives through integrated community development.
            </p>
            <p className="text-[#D4A843] font-playfair italic text-sm mb-3">
              Empowering Lives. Building Futures.
            </p>
            <p className="text-white/50 text-xs font-outfit">
              (Registered Public Charitable Trust)<br />
              Regd. No: JH/2025/0925229
            </p>
          </div>

          <div>
            <h4 className="font-playfair text-lg font-semibold mb-6 text-[#D4A843]">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-white/70 hover:text-[#D4A843] text-sm font-outfit transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-playfair text-lg font-semibold mb-6 text-[#D4A843]">
              Contact Info
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#D4A843] mt-0.5 flex-shrink-0" />
                <span className="text-white/70 text-sm font-outfit">
                  Jharkhand, India<br />
                  Primary Operations: Khunti, Ranchi,<br />
                  Lohardaga, Hazaribagh
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#D4A843] flex-shrink-0" />
                <a href="mailto:contact@mbtwelfaretrust.org" className="text-white/70 hover:text-[#D4A843] text-sm font-outfit transition-colors">
                  contact@mbtwelfaretrust.org
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#D4A843] flex-shrink-0" />
                <a href="tel:+919939281943" className="text-white/70 hover:text-[#D4A843] text-sm font-outfit transition-colors">
                  +91 99392 81943
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-playfair text-lg font-semibold mb-6 text-[#D4A843]">
              Connect With Us
            </h4>
            <div className="flex gap-3 mb-8">
              {[
                { name: "facebook", url: "https://facebook.com" },
                { name: "instagram", url: "https://www.instagram.com/mbt_trust/" },
                { name: "twitter", url: "https://twitter.com" },
                { name: "youtube", url: "https://youtube.com" },
                { name: "linkedin", url: "https://linkedin.com" },
              ].map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${social.name}`}
                  className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center hover:bg-[#D4A843] hover:text-[#1B4332] transition-all text-white/70"
                >
                  <SocialIcon name={social.name} />
                </a>
              ))}
            </div>
            <h4 className="font-playfair text-sm font-semibold mb-3 text-white/90">
              Newsletter
            </h4>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="Your email"
                data-testid="footer-newsletter-input"
                className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#D4A843] font-outfit"
              />
              <button
                data-testid="footer-newsletter-submit"
                className="bg-[#D4A843] text-[#1B4332] px-4 py-2 rounded-lg text-sm font-semibold font-outfit hover:bg-[#D4A843]/90 transition-colors"
              >
                Join
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-white/50 text-xs font-outfit text-center leading-relaxed">
            &copy; 2026 MBT Welfare & Development Trust (Registered Public Charitable Trust). All rights reserved.
            Regd. No: JH/2025/0925229 | Registered under the Indian Trusts Act. Donations eligible for tax
            benefits under Section 80G.
          </p>
        </div>
      </div>
    </footer>
  );
}

function SocialIcon({ name }) {
  const icons = {
    facebook: <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />,
    instagram: <><rect width="20" height="20" x="2" y="2" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" x2="17.51" y1="6.5" y2="6.5" /></>,
    twitter: <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />,
    youtube: <><path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" /><path d="m10 15 5-3-5-3z" /></>,
    linkedin: <><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect width="4" height="12" x="2" y="9" /><circle cx="4" cy="4" r="2" /></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name]}
    </svg>
  );
}
